import {
  Box,
  Button,
  Center,
  Checkbox,
  CheckboxGroup,
  FormControl,
  FormLabel,
  Heading,
  Image,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  Radio,
  RadioGroup,
  Text,
  Textarea,
  useDisclosure
} from '@chakra-ui/react'
import { Chart, registerables } from 'chart.js'
import { useEffect, useState } from 'react'
import { Bar } from 'react-chartjs-2'
import { useParams, useNavigate } from 'react-router-dom'

import Counter from '../../../components/counter/Counter'
import { IAceleraMagentaAnswer } from '../../../interfaces/IAceleraMagentaAnswer'
import AceleraMagentaService from '../../../services/AceleraMagenta/AceleraMagentaService'
import AceleraMagentaViewPage from './AceleraMagentaViewPage'

Chart.register(...registerables)

interface IFormField {
  id: number
  title: string
  type: string
  required: boolean
  options: string
  groups: string
}

interface IChartData {
  name: string
  value: number
}

interface IMetrics {
  min: number
  max: number
  message: string
}

export default function AceleraMagentaStepPage() {
  const aceleraMagentaService = new AceleraMagentaService()
  const navigate = useNavigate()
  const id = parseInt(useParams().id as string)
  const [form, setForm] = useState<IFormField[]>([])
  const [formTitle, setFormTitle] = useState<string>('')
  const [formAnswers, setFormAnswers] = useState<IAceleraMagentaAnswer>({})
  const [starAnswers, setStarAnswers] = useState<any[]>([])
  const [avaliacaoArpe, setAvaliacaoArpe] = useState<any[]>([])
  const [saving, setSaving] = useState<boolean>(false)
  const [chartData, setChartData] = useState<IChartData[]>([])
  const [maturityLevels, setMaturityLevels] = useState<{ maturity: string; message: string }[]>([])
  const [step, setStep] = useState<number>(0)
  const [metrics, setMetrics] = useState<IMetrics[]>([])
  const [score, setScore] = useState<number>(0)
  const modalViewAnswers = useDisclosure()
  const [firstFiveStars, setFirstFiveStars] = useState<number>(0)

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setSaving(true)

    // valida se todos os campos foram preenchidos
    let hasEmptyField = false
    form.map((field) => {
      if (field.type === 'title') return

      if (!formAnswers[field.id] || (formAnswers[field.id] === '0' && field.type === 'stars')) {
        hasEmptyField = true
        setSaving(false)
        return
      }
    })

    if (hasEmptyField) {
      alert('Preencha todos os campos')
      return
    }

    if (step === 5) {
      return aceleraMagentaService.update(id, {}).then((response) => {
        if (response.data.success) {
          loadStep()
        }

        setSaving(false)
      })
    }

    aceleraMagentaService.update(id, formAnswers).then((response) => {
      if (response.data.success) {
        loadStep()
      }

      setSaving(false)
    })
  }

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [step])

  const loadStep = async () => {
    aceleraMagentaService.getStep(id).then((response) => {
      setFormAnswers({})
      setForm([])
      setStep(response.data.step)

      if (response.data.step === 3) {
        setFormTitle('Nível de Maturidade')
        setChartData(response.data.chart_data)
        setMaturityLevels(response.data.maturity_levels)

        return
      }

      if (response.data.step === 5) {
        setFormTitle('Delegação Eficaz')
        setMetrics(response.data.metrics)
        setScore(response.data.score)
        setForm(response.data.questions)
        const answers: IAceleraMagentaAnswer = {}
        for (const key in response.data.answers) {
          if (response.data.answers.hasOwnProperty(key)) {
            answers[parseInt(key)] = response.data.answers[key].answer
          }
        }
        setFormAnswers(answers)
      }

      if (response.data.step === 7) {
        setFormTitle('Feedback 180º Colaborador')
        setScore(Number(response.data.score.replace(/0{0,}$/, '').replace(/(\.|\,)$/, '')))
        setMetrics(response.data.metrics)
        setStarAnswers(response.data.answers)

        setFirstFiveStars(response.data.answers.find((answer: { answer: string }) => answer.answer === '5').id)
      }

      if (response.data.step === 9) {
        setFormTitle('Avaliação ARPE Colaborador')

        setAvaliacaoArpe(JSON.parse(response.data.score))

        setStarAnswers(response.data.answers)
        setFirstFiveStars(response.data.answers.find((answer: { answer: string }) => answer.answer === '5').id)
      }

      if (response.data.step === 14) {
        const nextAlignmentFieldId = response.data.questions.find((field: IFormField) => field.type === 'date')?.id

        if (nextAlignmentFieldId) {
          const today = new Date()
          const futureDate = new Date(today.getFullYear(), today.getMonth() + 6, today.getDate())
          const formattedDate =
            futureDate.getFullYear() +
            '-' +
            (futureDate.getMonth() + 1).toString().padStart(2, '0') +
            '-' +
            futureDate.getDate().toString().padStart(2, '0')

          setFormAnswers({ [nextAlignmentFieldId]: formattedDate })
        }
      }

      if (response.data.finished) {
        navigate('/acelera-magenta/complete')

        return
      }

      setFormTitle(response.data.form.name)
      setForm(response.data.questions)
    })
  }

  useEffect(() => {
    loadStep()
  }, [])

  return (
    <>
      <Center marginTop={5}>
        <Box borderWidth="1px" p="5" borderRadius="lg" w="51%" marginBottom="5">
          <Center>
            <Image src="/img/acelera-magenta.png" alt="Acelera Magenta" w="65%" marginBottom="10" />
          </Center>
          <Heading as="h1" size="lg" mb="4">
            {formTitle}
            {step == 14 && (
              <>
                <Button
                  colorScheme="blue"
                  variant="link"
                  onClick={() => modalViewAnswers.onOpen()}
                  size={'sm'}
                  float="right"
                  marginTop="3"
                >
                  Ver respostas anteriores
                </Button>
              </>
            )}
          </Heading>
          <form onSubmit={handleSubmit}>
            {step === 3 && (
              <>
                <Bar
                  data={{
                    labels: ['Bebê', 'Criança', 'Adolescente', 'Adulto'],
                    datasets: [
                      {
                        label: 'Nível de Maturidade',
                        data: chartData.map((data) => data.value),
                        backgroundColor: chartData.map((data) =>
                          isBiggest(
                            data.value,
                            chartData.map((d) => d.value)
                          )
                            ? 'rgba(250, 0, 105, 0.8)'
                            : 'rgba(51, 51, 51, 0.8)'
                        )
                      }
                    ]
                  }}
                />
                <br />

                {maturityLevels.map((level, key) => (
                  <Box
                    key={level.maturity}
                    borderWidth="1px"
                    p="5"
                    borderRadius="lg"
                    w="100%"
                    marginBottom="3"
                    backgroundColor={
                      isBiggest(
                        chartData[key].value,
                        chartData.map((d) => d.value)
                      )
                        ? 'rgba(250, 0, 105)'
                        : 'rgba(51, 51, 51)'
                    }
                    color="#FFFFFF"
                  >
                    <Heading as="h3" size="sm" mb="4">
                      {level.maturity}
                    </Heading>
                    <div
                      dangerouslySetInnerHTML={{
                        __html: level.message.replaceAll('\n', '<br>')
                      }}
                    />
                  </Box>
                ))}
                <br />
              </>
            )}

            {step === 5 && (
              <>
                <Box marginBottom="5">
                  <Box borderWidth="1px" p="5" borderRadius="lg" w="100%">
                    <Heading as="h3" size="sm" mb="4">
                      Pontuação
                    </Heading>
                    <Box
                      borderWidth="1px"
                      p="5"
                      borderRadius="lg"
                      w="100%"
                      marginBottom="3"
                      backgroundColor="#FA0069"
                      color="#FFFFFF"
                    >
                      <Heading as="h4" size="lg">
                        <Center>
                          <Counter limit={score} />
                          &nbsp;pontos!
                        </Center>
                      </Heading>
                    </Box>

                    {metrics.map((metric) => (
                      <Box
                        key={metric.min}
                        borderWidth="1px"
                        p="5"
                        borderRadius="lg"
                        w="100%"
                        marginBottom="3"
                        backgroundColor={
                          score >= metric.min && score <= metric.max ? 'rgba(250, 0, 105)' : 'rgba(51, 51, 51)'
                        }
                        color="#FFFFFF"
                      >
                        <div
                          dangerouslySetInnerHTML={{
                            __html: `De ${metric.min} a ${metric.max} respostas corretas - ${metric.message.replaceAll(
                              '\n',
                              '<br>'
                            )}`
                          }}
                        />
                      </Box>
                    ))}
                  </Box>
                </Box>
                <Text>Resultado das respostas:</Text>
              </>
            )}

            {step === 7 && (
              <>
                <Box marginBottom="5">
                  <Box borderWidth="1px" p="5" borderRadius="lg" w="100%">
                    <Heading as="h3" size="sm" mb="4">
                      Média das avaliações
                    </Heading>
                    <Box
                      borderWidth="1px"
                      p="5"
                      borderRadius="lg"
                      w="100%"
                      marginBottom="3"
                      backgroundColor="#FA0069"
                      color="#FFFFFF"
                    >
                      <Heading as="h4" size="lg">
                        <Center>{score?.toString()?.replace('.', ',') || 0} estrelas!</Center>
                      </Heading>
                    </Box>

                    {metrics.map(
                      (metric) =>
                        score >= metric.min &&
                        score <= metric.max && (
                          <Box
                            key={metric.min}
                            borderWidth="1px"
                            p="5"
                            borderRadius="lg"
                            w="100%"
                            marginBottom="3"
                            backgroundColor={
                              score >= metric.min && score <= metric.max ? 'rgba(250, 0, 105)' : 'rgba(51, 51, 51)'
                            }
                            color="#FFFFFF"
                          >
                            <div
                              dangerouslySetInnerHTML={{
                                __html: metric.message.replaceAll('\n', '<br>')
                              }}
                            />
                          </Box>
                        )
                    )}
                  </Box>
                </Box>
                <Text>Resultado das respostas:</Text>
                {starAnswers.map((answer) => {
                  return (
                    <>
                      {answer.id === firstFiveStars && (
                        <>
                          <hr />
                          <Text mb="3">
                            <strong>Parabéns pelas 5 estrelas nos itens abaixo!</strong>
                          </Text>
                        </>
                      )}
                      <FormControl isRequired={false} key={answer.id} mb="4">
                        {answer.question.title.replace(/\s/, '') && <FormLabel>{answer.question.title}</FormLabel>}
                        <StarRating
                          rating={parseInt(answer.answer)}
                          // eslint-disable-next-line @typescript-eslint/no-empty-function
                          onChange={() => {}}
                        />
                      </FormControl>
                    </>
                  )
                })}
              </>
            )}

            {step === 9 && (
              <>
                Notas ARPE
                {Object.keys(avaliacaoArpe).map((key: string) => {
                  const value: number = avaliacaoArpe[key as any]
                  return (
                    <>
                      <p>
                        <b>{key}</b> - {Number(value).toFixed(2)} estrelas
                      </p>
                    </>
                  )
                })}
                <>
                  <Box marginBottom="5" marginTop="5">
                    <Box borderWidth="1px" p="5" borderRadius="lg" w="100%">
                      <Heading as="h3" size="sm" mb="4">
                        <Center>Nota final ARPE</Center>
                      </Heading>
                      <Box
                        borderWidth="1px"
                        p="5"
                        borderRadius="lg"
                        w="100%"
                        marginBottom="3"
                        backgroundColor="#FA0069"
                        color="#FFFFFF"
                      >
                        <Heading as="h4" size="lg">
                          <Center>
                            {(Object.values(avaliacaoArpe).reduce((a: number, b: number) => a + b, 0) / 4).toFixed(2)}{' '}
                            estrelas!
                          </Center>
                        </Heading>
                      </Box>
                    </Box>
                  </Box>
                  <Text>Resultado das respostas:</Text>
                  {starAnswers.map((answer) => {
                    return (
                      <>
                        {answer.id === firstFiveStars && (
                          <>
                            <hr />
                            <Text mb="3">
                              <strong>Parabéns pelas 5 estrelas nos itens abaixo!</strong>
                            </Text>
                          </>
                        )}
                        <FormControl isRequired={false} key={answer.id} mb="4">
                          {answer.question.title.replace(/\s/, '') && <FormLabel>{answer.question.title}</FormLabel>}
                          <StarRating
                            rating={parseInt(answer.answer)}
                            // eslint-disable-next-line @typescript-eslint/no-empty-function
                            onChange={() => {}}
                          />
                        </FormControl>
                      </>
                    )
                  })}
                </>
              </>
            )}

            {form.map((field) => {
              return (
                <FormControl isRequired={step != 5 && field.type !== 'title'} key={field.id} mb="4">
                  {field.title.replace(/\s/, '') && <FormLabel>{field.title}</FormLabel>}
                  {field.type === 'textarea' && (
                    <Textarea
                      value={formAnswers[field.id]}
                      onChange={(event) => setFormAnswers({ ...formAnswers, [field.id]: event.target.value })}
                      height="180px"
                    />
                  )}
                  {field.type === 'date' && (
                    <Input
                      type="date"
                      value={formAnswers[field.id]}
                      onChange={(event) => setFormAnswers({ ...formAnswers, [field.id]: event.target.value })}
                    />
                  )}
                  {field.type === 'options' && (
                    <RadioGroup
                      value={formAnswers[field.id]}
                      onChange={(value) => (step !== 5 ? setFormAnswers({ ...formAnswers, [field.id]: value }) : '')}
                    >
                      {(JSON.parse(field.options) as string[]).map((option, index) => (
                        <Radio value={(index + 1).toString()} key={index} marginLeft="2">
                          {step !== 5 && option}

                          {step === 5 &&
                            formAnswers[field.id] == (index + 1).toString() &&
                            ((JSON.parse(field.groups)[parseInt(formAnswers[field.id]) - 1] == 1 && (
                              <Text color="green" fontWeight="semibold">
                                {option}
                              </Text>
                            )) ||
                              (JSON.parse(field.groups)[parseInt(formAnswers[field.id]) - 1] != 1 && (
                                <Text color="tomato" fontWeight="semibold">
                                  {option}
                                </Text>
                              )))}
                          {step === 5 && formAnswers[field.id] != (index + 1).toString() && option}
                        </Radio>
                      ))}
                    </RadioGroup>
                  )}
                  {field.type === 'checkbox' && (
                    <CheckboxGroup
                      value={formAnswers[field.id] ? JSON.parse(formAnswers[field.id]) : []}
                      onChange={(value) => setFormAnswers({ ...formAnswers, [field.id]: JSON.stringify(value) })}
                    >
                      {(JSON.parse(field.options) as string[]).map((option, index) => (
                        <Checkbox value={(index + 1).toString()} key={index} marginLeft="2">
                          {option}
                        </Checkbox>
                      ))}
                    </CheckboxGroup>
                  )}
                  {field.type === 'stars' && (
                    <>
                      {!field.required && (
                        <Button
                          colorScheme="gray"
                          isActive={formAnswers[field.id] == '-1'}
                          type="button"
                          size={'xs'}
                          style={{ float: 'right' }}
                          onClick={() =>
                            setFormAnswers({ ...formAnswers, [field.id]: formAnswers[field.id] == '-1' ? '0' : '-1' })
                          }
                        >
                          Não se aplica
                        </Button>
                      )}
                      <StarRating
                        rating={parseInt(formAnswers[field.id] ?? '0')}
                        onChange={(value) => setFormAnswers({ ...formAnswers, [field.id]: value.toString() })}
                      />
                      <div style={{ clear: 'both' }}></div>
                    </>
                  )}
                </FormControl>
              )
            })}

            {step < 10 ? (
              <Button colorScheme="magenta" type="submit" disabled={saving}>
                Próximo passo &nbsp;<i className="lni lni-arrow-right"></i>
              </Button>
            ) : (
              <Button colorScheme="magenta" type="submit" disabled={saving}>
                Assinar contrato &nbsp;<i className="lni lni-checkmark"></i>
              </Button>
            )}
          </form>
        </Box>
      </Center>

      <Modal isOpen={modalViewAnswers.isOpen} onClose={modalViewAnswers.onClose} size="4xl">
        <ModalOverlay />
        <ModalContent>
          <ModalCloseButton />
          <ModalBody paddingTop="10" paddingBottom="10">
            <AceleraMagentaViewPage aceleraMagentaId={id} skipLastStep={true} />
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  )
}

const StarRating = ({ rating, onChange }: { rating: number; onChange: (value: number) => void }) => {
  const starIcons = []

  if (rating === -1) {
    return (
      <>
        <i style={{ color: '#656565' }}>Não se aplica</i>
      </>
    )
  }

  for (let i = 1; i <= 5; i++) {
    if (i <= rating) {
      starIcons.push(
        <i key={i} className="lni lni-star-fill" style={{ color: '#fa0069' }} onClick={() => onChange(i)}></i>
      )
    } else {
      starIcons.push(<i key={i} className="lni lni-star-empty" onClick={() => onChange(i)}></i>)
    }
  }

  return <div style={{ fontSize: '1.4rem' }}>{starIcons}</div>
}

const getHexColor = (number: number) => {
  const startColor = [51, 51, 51] // equivalent to #333333 in RGB
  const endColor = [250, 0, 105] // equivalent to #fa0069 in RGB
  const ratio = number / 20

  const color = startColor.map((start, index) => {
    const end = endColor[index]
    const value = Math.round((1 - ratio) * start + ratio * end)
    return value.toString(16).padStart(2, '0')
  })

  return `#${color.join('')}`
}

const isBiggest = (number: number, array: number[]) => {
  return number == Math.max(...array)
}
